import { FC, ReactNode, useEffect } from "react";
import { motion } from "framer-motion";

import "./WavesBackground.css";

import { useWindowSize } from "../utils/useWindowSize";
import { useStorage } from "../utils/useStorage";
import { useCache } from "../utils/useCache";

interface WavesBackgroundProps {
  progress: number;
  children: ReactNode;
  onAnimationEnd?: () => void;
  onAnimationStart?: () => void;
}

const getContainerHeight = (progress: number, waveHeight: string) => {
  if (progress <= 0) {
    return "0%";
  }

  if (progress >= 1) {
    return "100%";
  }

  return `calc(${progress * 100}% - (${waveHeight} / 2))`
};
const getWaveHeight = (progress: number, screenHeightPoint: number) => {
  const base = 0.5 - Math.abs(0.5 - progress);

  if (base < 0) {
    return "0px";
  }

  return `${base * 100 * screenHeightPoint}px`
};

export const WavesBackground: FC<WavesBackgroundProps> = ({ children, progress, onAnimationEnd, onAnimationStart }) => {
  const { isReady } = useStorage();
  const { previousWavesStyle, setPreviousWavesStyle } = useCache();
  const { innerHeight } = useWindowSize();

  const screenHeightPoint = innerHeight / 100;

  const waveHeight = getWaveHeight(progress, screenHeightPoint);
  const containerHeight = getContainerHeight(progress, waveHeight);

  useEffect(() => {
    setPreviousWavesStyle && setPreviousWavesStyle({
      wave: waveHeight,
      container: containerHeight
    });
  }, [waveHeight, containerHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  const containerFrom = {
    height: isReady ? previousWavesStyle.container : "50%"
  }

  const containerTo = {
    height: containerHeight
  }

  const waveFrom = {
    height: isReady ? previousWavesStyle.wave : 50 * screenHeightPoint
  }

  const waveTo = {
    height: waveHeight
  };

  return (
    <div className="waves-background">
      <div className="waves-background__content">
        {children}
      </div>
      <motion.div
        className="waves-background__progress"
        initial={containerFrom}
        animate={containerTo}
        transition={{ duration: 1, ease: "easeIn" }}
        onAnimationComplete={onAnimationEnd}
        onAnimationStart={onAnimationStart}
      >
        <motion.svg
          className="waves-animation"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          viewBox="0 24 150 28"
          preserveAspectRatio="none"
          shapeRendering="auto"
          initial={waveFrom}
          animate={waveTo}
          transition={{ duration: 1, ease: "easeIn" }}
        >
          <defs>
            <path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
          </defs>
          <g className="waves-animation__parallax">
            <use xlinkHref="#gentle-wave" x="48" y="0" fill="rgba(255,255,255,0.55)" />
            <use xlinkHref="#gentle-wave" x="48" y="3" fill="rgba(255,255,255,0.35)" />
            <use xlinkHref="#gentle-wave" x="48" y="5" fill="rgba(255,255,255,0.15)" />
            <use xlinkHref="#gentle-wave" x="48" y="7" fill="#fff" />
          </g>
        </motion.svg>
      </motion.div>
    </div >
  );
}